import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createLead, updateLead } from "../../services/leadService";
import styles from "./CreateLeadModal.module.css";
import Modal from "../Modal/Modal";

/** Rota da vitrine de planos — mesma usada por `usePlanFeature().upgradeUrl`. */
const UPGRADE_URL = "/subscription/plans";

const SOURCE_OPTIONS = [
  { value: "", label: "— Selecione —" },
  { value: "Indicação de cliente", label: "Indicação de cliente" },
  { value: "Redes sociais", label: "Redes sociais" },
  { value: "Passou na frente da loja", label: "Passou na frente da loja" },
  { value: "Google", label: "Google" },
  { value: "Outro", label: "Outro" },
];

/**
 * CreateLeadModal — modal de criação ou edição de Lead.
 *
 * Props:
 *   lead       {Object|null}   — se fornecido, modal entra em modo edição
 *   onClose    {() => void}
 *   onSuccess  {(lead) => void}
 */
export default function CreateLeadModal({ lead = null, onClose, onSuccess }) {
  const isEdit = Boolean(lead?.id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    source: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Limite de leads do plano estourado (403 `LIMIT_REACHED` no POST /api/leads).
  // Vira um banner com CTA de upgrade em vez de toast genérico.
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        name: lead.name || "",
        phone: lead.phone || "",
        email: lead.email || "",
        source: lead.source || "",
        notes: lead.notes || "",
      });
    }
  }, [lead]);

  /**
   * Se o lead em edição tem `source` com valor que NÃO está nas opções
   * predefinidas (campo legado vindo do banco), adicionamos uma opção extra
   * preservando o valor — assim o select não fica vazio nem perde o valor ao salvar.
   */
  const sourceOptions = useMemo(() => {
    const known = SOURCE_OPTIONS.some((opt) => opt.value === form.source);
    if (form.source && !known) {
      return [
        ...SOURCE_OPTIONS,
        {
          value: form.source,
          label: `${form.source} (valor anterior)`,
        },
      ];
    }
    return SOURCE_OPTIONS;
  }, [form.source]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setForm((f) => ({ ...f, phone: maskBRPhone(value) }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Informe o nome do lead";

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "E-mail inválido";
    }

    // Telefone é opcional, mas se preenchido precisa ter 10 (fixo) ou 11 (celular) dígitos.
    if (form.phone.trim()) {
      const digits = form.phone.replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 11) {
        next.phone = "Telefone incompleto. Use DDD + número (10 ou 11 dígitos).";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      setLimitReached(false);
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        source: form.source || undefined,
        notes: form.notes.trim() || undefined,
      };
      let res;
      if (isEdit) {
        res = await updateLead(lead.id, payload);
        toast.success("Lead atualizado!");
      } else {
        res = await createLead(payload);
        toast.success("Lead cadastrado!");
      }
      onSuccess?.(res?.data);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      // Limite de leads do plano (Básico 100 / Profissional 500) — a contagem é
      // do backend; aqui só traduzimos o 403 numa mensagem clara + CTA.
      // Mesmo padrão de `ProfessionalsNew.js` (403 + `code === "LIMIT_REACHED"`).
      if (status === 403 && data?.code === "LIMIT_REACHED") {
        setLimitReached(true);
        toast.error("Você atingiu o limite de leads do seu plano.");
      } else {
        const msg =
          data?.error ||
          data?.message ||
          (isEdit ? "Erro ao atualizar lead." : "Erro ao criar lead.");
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} width="480px" titleId="lead-form-modal-title">
      <div className={styles.modalInner}>
        <h2 id="lead-form-modal-title" className={styles.title}>
          {isEdit ? "Editar lead" : "Cadastrar novo lead"}
        </h2>
        <p className={styles.subtitle}>
          {isEdit
            ? "Atualize os dados do lead abaixo."
            : "Só o nome é obrigatório. O resto você pode preencher depois."}
        </p>

        {limitReached && (
          <div className={styles.limitBanner} role="alert">
            <p className={styles.limitTitle}>
              Você atingiu o limite de leads do seu plano
            </p>
            <p className={styles.limitText}>
              Para continuar cadastrando potenciais clientes, faça um upgrade —
              ou remova leads antigos que já não estão no funil.
            </p>
            <button
              type="button"
              className={styles.limitBtn}
              onClick={() => navigate(UPGRADE_URL)}
            >
              Ver planos disponíveis
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <label className={styles.label} htmlFor="lead-name">
            Nome <span className={styles.required} aria-hidden="true">*</span>
          </label>
          <input
            id="lead-name"
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Ex.: João Silva"
            className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "lead-name-err" : undefined}
            autoFocus
            required
          />
          {errors.name && (
            <span id="lead-name-err" className={styles.errorMsg}>
              {errors.name}
            </span>
          )}

          <label className={styles.label} htmlFor="lead-phone">
            Telefone
          </label>
          <input
            id="lead-phone"
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="(11) 99999-9999"
            className={`${styles.input} ${errors.phone ? styles.inputError : ""}`}
            inputMode="tel"
            maxLength={16}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "lead-phone-err" : undefined}
          />
          {errors.phone && (
            <span id="lead-phone-err" className={styles.errorMsg}>
              {errors.phone}
            </span>
          )}

          <label className={styles.label} htmlFor="lead-email">
            E-mail
          </label>
          <input
            id="lead-email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="email@exemplo.com"
            className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "lead-email-err" : undefined}
          />
          {errors.email && (
            <span id="lead-email-err" className={styles.errorMsg}>
              {errors.email}
            </span>
          )}

          <label className={styles.label} htmlFor="lead-source">
            De onde veio?
          </label>
          <select
            id="lead-source"
            name="source"
            value={form.source}
            onChange={handleChange}
            className={styles.input}
          >
            {sourceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <label className={styles.label} htmlFor="lead-notes">
            Observações
          </label>
          <textarea
            id="lead-notes"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            className={styles.textarea}
            placeholder="Anote o que for útil para o atendimento (preferências, urgência, etc.)"
            rows={3}
          />

          <div className={styles.actions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelBtn}
              disabled={loading}
            >
              Cancelar
            </button>
            <button type="submit" disabled={loading} className={styles.submitBtn}>
              {loading
                ? isEdit
                  ? "Salvando…"
                  : "Cadastrando…"
                : isEdit
                ? "Salvar alterações"
                : "Cadastrar lead"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

/**
 * Aplica máscara brasileira de telefone: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX.
 * Aceita parcialmente — vai construindo conforme o usuário digita.
 */
function maskBRPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    // Fixo: (XX) XXXX-XXXX
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  // Celular: (XX) XXXXX-XXXX
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
